import React, { FC, useEffect, useRef } from 'react';
import { backofficeUrl, stringFormat, T } from 'src/shared/helpers';
import { useNavigate } from 'react-router-dom';
import usePermissions from 'src/shared/hooks/usePermissions';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import { AiOutlineButton } from '../ai';
import { ActivateAddonDialog } from '../activateAddonDialog/activateAddonDialog';
import { useDialogManager } from '../dialog';
import { AgentRequirementControls } from './AgentRequirementControls';
import UpgradeDialog from '../upgradeDialog/upgradeDialog';
import { AgentVectorizerCreditsDialog } from './AgentVectorizerCreditsDialog';
import { useFreeAICreditsUsedUp } from 'src/components/pages/aiStudio/components/dialogs/aiStudioNoCreditsDialog';
import { useAgentAssistant } from './AgentAssistantContext';

interface AgentAddonActionCardProps {
    addonKey: string;
    action?: string | null;
    busy: boolean;
    hasDraft?: boolean;
    onResume: () => void | Promise<void>;
    /** AI Studio activation also grants its first credit pack, so it uses its existing preflight. */
    onActivate?: () => void;
}

export const AgentAddonActionCard: FC<AgentAddonActionCardProps> = ({ addonKey, action, busy, onResume, onActivate, hasDraft = true }) => {
    const { subscription, sellerMarketplace, updateSubscription, activateAddonForSeller } = useSubscriptionData();
    const { isAllowed } = usePermissions();
    const { showDialog, closeDialog } = useDialogManager();
    const freeCredits = useFreeAICreditsUsedUp();
    const navigate = useNavigate();
    const { close } = useAgentAssistant();
    const refresh = useRef(updateSubscription);
    refresh.current = updateSubscription;

    useEffect(() => {
        const onFocus = () => refresh.current();
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, []);

    const addonName =
        addonKey === 'LARGE_FILES'
            ? T._('Large files', 'Admin')
            : addonKey === 'AI_STUDIO'
              ? T._('AI Studio', 'Admin')
              : addonKey === 'VECTORIZE'
                ? T._('AI Vectorizer', 'Admin')
                : addonKey.replace(/_/g, ' ');
    const hasAddon = subscription?.addons.some((addon) => addon.name === addonKey);
    // The requested action is supplied by Subscription, rather than inferred from a feature or entity.
    const planSupportsAddon = action !== 'upgrade_plan';
    const enabled = hasAddon && !action;
    const canManageSubscription = isAllowed(['SUBSCRIPTION']);

    const resume = () => {
        refresh.current();
        void onResume();
    };

    const manageSubscription = () => {
        close();
        navigate(backofficeUrl('/seller/subscription'));
    };

    const activate = () => {
        if (addonKey === 'VECTORIZE') {
            const dialogID = 'agent-vectorizer-credits';
            showDialog(dialogID, <AgentVectorizerCreditsDialog onClose={() => closeDialog(dialogID)} onReady={resume} />);
            return;
        }
        if (onActivate) {
            onActivate();
            return;
        }
        showDialog(
            `agent-activate-${addonKey.toLowerCase()}`,
            <ActivateAddonDialog
                addonKey={addonKey}
                addonName={addonName}
                message={stringFormat(T._('Activate the {0} add-on to continue this configuration.', 'Admin'), addonName)}
                onActivateCompleted={resume}
            />
        );
    };

    // AI credit model (SubscriptionService free_credits_used_up): no add-on to activate and no credits to buy. The
    // same explanation and upgrade of the no-credits dialog; checking again resumes after the upgrade.
    if (action === 'free_credits_used_up') {
        return (
            <AgentRequirementControls disabled={busy}>
                <span>
                    {freeCredits.message} {hasDraft && T._('Your draft is saved.', 'Admin')}
                    {canManageSubscription &&
                        freeCredits.endsShopifyTrial &&
                        ` ${T._('Continuing will end your free trial now and switch you to your paid plan right away: Shopify will ask you to approve the charge. After that, AI usage is billed with your plan.', 'Admin')}`}
                    {!canManageSubscription && ` ${T._('Ask the account owner or a user with subscription permissions to upgrade the plan.', 'Admin')}`}
                </span>
                {canManageSubscription && (
                    <AiOutlineButton
                        type='button'
                        $size='small'
                        disabled={busy}
                        onClick={() => {
                            if (!freeCredits.isShopifyDevStore && freeCredits.canUpgradeTrial) {
                                close();
                                freeCredits.upgradeTrial();
                            } else manageSubscription();
                        }}
                    >
                        {T._('Switch to a paid account', 'Admin')}
                    </AiOutlineButton>
                )}
                <AiOutlineButton type='button' $size='small' disabled={busy} onClick={resume}>
                    {T._('Check again', 'Admin')}
                </AiOutlineButton>
            </AgentRequirementControls>
        );
    }

    // Old turns may retain a purchase action from the previous credit model. Recheck the
    // account instead of offering a credit purchase that no longer authorizes AI usage.
    if (action === 'purchase_credits') {
        return (
            <AgentRequirementControls disabled={busy}>
                <span>
                    {canManageSubscription
                        ? T._('AI usage is managed through your subscription. Check availability again to review the current account requirements.', 'Admin')
                        : T._('Ask the account owner or a user with subscription permissions to review the subscription.', 'Admin')}
                </span>
                {canManageSubscription && (
                    <AiOutlineButton type='button' $size='small' disabled={busy} onClick={manageSubscription}>
                        {T._('Manage subscription', 'Admin')}
                    </AiOutlineButton>
                )}
                <AiOutlineButton type='button' $size='small' disabled={busy} onClick={resume}>
                    {T._('Check again', 'Admin')}
                </AiOutlineButton>
            </AgentRequirementControls>
        );
    }

    return (
        <AgentRequirementControls disabled={busy}>
            {enabled ? (
                <span>{stringFormat(T._('{0} is active. Resume to review the configuration before applying it.', 'Admin'), addonName)}</span>
            ) : !canManageSubscription ? (
                <span>
                    {stringFormat(
                        T._('The {0} add-on is required. Ask the account owner or a user with subscription permissions to activate it.', 'Admin'),
                        addonName
                    )}
                </span>
            ) : !planSupportsAddon ? (
                <UpgradeDialog
                    type='BANNER'
                    marketPlace={sellerMarketplace ?? undefined}
                    redirectUrlFromDashboard={encodeURI(window.location.href)}
                    showUpgradeButtonAsLink
                    addonKeyToActivate={addonKey}
                    addonNameToActivate={addonName}
                    buttonUpgradeText={T._('Upgrade & Add', 'Admin')}
                    message={stringFormat(
                        T._('Your plan does not support {0}. Upgrade your plan and activate the add-on to continue. Your draft is saved.', 'Admin'),
                        addonName
                    )}
                    onUpgradeCompleted={async () => {
                        activateAddonForSeller(addonKey, subscription?.plan?.currencyCode ?? 'USD', resume);
                    }}
                />
            ) : (
                <>
                    <span>
                        {stringFormat(T._('Activate the {0} add-on to continue. Your draft is saved.', 'Admin'), addonName)}
                    </span>
                    <AiOutlineButton type='button' $size='small' disabled={busy} onClick={activate}>
                        {stringFormat(T._('Activate {0}', 'Admin'), addonName)}
                    </AiOutlineButton>
                </>
            )}
            {(enabled || !onActivate) && (
                <AiOutlineButton type='button' $size='small' disabled={busy} onClick={resume}>
                    {enabled ? T._('Resume configuration', 'Admin') : T._('Check add-on availability', 'Admin')}
                </AiOutlineButton>
            )}
        </AgentRequirementControls>
    );
};
